import { useEffect, useRef, useState } from 'react'
import { Alert, Button, Drawer, Input, Select, Space, Switch, Table, Tag, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useToast } from '../../contexts/ToastContext'
import { useRuleSet, useSaveRuleSetMutation, useViewTemplates } from '../../hooks/useFileUploadRules'
import { extractRuleSetErrors } from '../../api/fileUploadRules'
import type { RuleEntry, ViewGrant } from '../../types/fileUploadRules'
import RuleEntryModal from './RuleEntryModal'
import { entryKey, formatConfigSummary, validateEntry } from './ruleConfig'

type Props = {
  open: boolean
  // Table name when editing an existing set; null when creating a new one.
  editingTableName: string | null
  // Existing set table names, used to block creating a duplicate set.
  existingTableNames: string[]
  onClose: () => void
  onSaved: () => void
}

export default function RuleSetEditorDrawer({
  open,
  editingTableName,
  existingTableNames,
  onClose,
  onSaved,
}: Props) {
  const { addToast } = useToast()
  const isEdit = editingTableName != null
  const ruleSetQuery = useRuleSet(editingTableName ?? undefined, open && isEdit)
  const saveMutation = useSaveRuleSetMutation()
  const [hasViews, setHasViews] = useState(false)
  const viewTemplatesQuery = useViewTemplates(open && hasViews)

  const [tableName, setTableName] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [viewName, setViewName] = useState('')
  const [viewDefinition, setViewDefinition] = useState('')
  const [viewOwner, setViewOwner] = useState('')
  const [viewGrants, setViewGrants] = useState<ViewGrant[]>([])
  const [rules, setRules] = useState<RuleEntry[]>([])
  const [serverErrors, setServerErrors] = useState<string[]>([])
  const [entryModalOpen, setEntryModalOpen] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const initializedRef = useRef(false)

  // Initialize form state once per open session (and again when the fetched set arrives).
  useEffect(() => {
    if (!open) {
      initializedRef.current = false
      return
    }
    if (isEdit) {
      if (ruleSetQuery.data && !initializedRef.current) {
        setTableName(ruleSetQuery.data.tableName)
        setIsActive(ruleSetQuery.data.isActive)
        setHasViews(ruleSetQuery.data.hasViews)
        setViewName(ruleSetQuery.data.viewName ?? '')
        setViewDefinition(ruleSetQuery.data.viewDefinition ?? '')
        setViewOwner(ruleSetQuery.data.viewOwner ?? '')
        setViewGrants(ruleSetQuery.data.viewGrants ?? [])
        setRules(ruleSetQuery.data.rules)
        setServerErrors([])
        initializedRef.current = true
      }
    } else if (!initializedRef.current) {
      setTableName('')
      setIsActive(true)
      setHasViews(false)
      setViewName('')
      setViewDefinition('')
      setViewOwner('')
      setViewGrants([])
      setRules([])
      setServerErrors([])
      initializedRef.current = true
    }
  }, [open, isEdit, ruleSetQuery.data])

  const openAddEntry = () => {
    setEditingIndex(null)
    setEntryModalOpen(true)
  }

  const openEditEntry = (index: number) => {
    setEditingIndex(index)
    setEntryModalOpen(true)
  }

  const handleEntrySave = (entry: RuleEntry) => {
    setRules((prev) => {
      if (editingIndex == null) return [...prev, entry]
      return prev.map((existing, index) => (index === editingIndex ? entry : existing))
    })
    setEntryModalOpen(false)
  }

  const removeEntry = (index: number) => {
    setRules((prev) => prev.filter((_, i) => i !== index))
  }

  const addGrant = () => setViewGrants((prev) => [...prev, { privilege: '', grantee: '' }])
  const updateGrant = (index: number, patch: Partial<ViewGrant>) =>
    setViewGrants((prev) => prev.map((grant, i) => (i === index ? { ...grant, ...patch } : grant)))
  const removeGrant = (index: number) => setViewGrants((prev) => prev.filter((_, i) => i !== index))

  const handleSave = async () => {
    const trimmedTable = tableName.trim()
    const errors: string[] = []
    if (!trimmedTable) errors.push('Table name is required.')
    if (
      !isEdit &&
      existingTableNames.map((t) => t.toLowerCase()).includes(trimmedTable.toLowerCase())
    ) {
      errors.push('A rule set already exists for this table. Edit that set instead.')
    }
    if (rules.length === 0) errors.push('Add at least one rule to the set.')
    if (hasViews && !viewName.trim()) errors.push('View name is required when the table has a dependent view.')
    if (hasViews && viewGrants.some((grant) => Boolean(grant.privilege.trim()) !== Boolean(grant.grantee.trim()))) {
      errors.push('Each grant needs both a privilege and a grantee.')
    }
    rules.forEach((rule, index) => errors.push(...validateEntry(rule, index)))

    if (errors.length) {
      setServerErrors(errors)
      return
    }

    try {
      setServerErrors([])
      await saveMutation.mutateAsync({
        tableName: trimmedTable,
        isActive,
        hasViews,
        viewName: hasViews ? viewName.trim() : undefined,
        viewDefinition: hasViews ? viewDefinition.trim() : undefined,
        viewOwner: hasViews ? viewOwner.trim() : undefined,
        viewGrants: hasViews ? viewGrants : undefined,
        rules,
      })
      addToast('Rule set saved.', 'success')
      onSaved()
      onClose()
    } catch (error) {
      const apiErrors = extractRuleSetErrors(error)
      if (apiErrors.length) setServerErrors(apiErrors)
      addToast(error instanceof Error ? error.message : 'Failed to save rule set', 'error')
    }
  }

  const takenKeys = rules
    .filter((_, index) => index !== editingIndex)
    .map((rule) => entryKey(rule.columnName, rule.ruleType))

  const columns: ColumnsType<RuleEntry> = [
    { title: 'Column', dataIndex: 'columnName', key: 'columnName' },
    {
      title: 'Rule Type',
      dataIndex: 'ruleType',
      key: 'ruleType',
      render: (value: string) => <Tag>{value}</Tag>,
    },
    {
      title: 'Config',
      key: 'config',
      render: (_value, row) => <span className="text-gray-600">{formatConfigSummary(row)}</span>,
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      render: (value: string) => <Tag color={value === 'Error' ? 'red' : 'gold'}>{value}</Tag>,
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (value: boolean) =>
        value ? <Tag color="green">On</Tag> : <Tag>Off</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'right',
      render: (_value, _row, index) => (
        <Space>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEditEntry(index)} />
          </Tooltip>
          <Tooltip title="Remove">
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeEntry(index)} />
          </Tooltip>
        </Space>
      ),
    },
  ]

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={840}
      title={isEdit ? 'Edit Rule Set' : 'Add Rule Set'}
      extra={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={() => void handleSave()} loading={saveMutation.isPending}>
            Save Rule Set
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size={16} className="w-full">
        {serverErrors.length > 0 && (
          <Alert
            type="error"
            showIcon
            message="This rule set could not be saved"
            description={
              <ul className="list-disc pl-4">
                {serverErrors.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            }
          />
        )}

        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div style={{ minWidth: 280 }}>
            <label className="block text-sm font-medium text-gray-700 mb-1">Table name</label>
            <Input
              value={tableName}
              onChange={(e) => setTableName(e.target.value)}
              disabled={isEdit}
              placeholder="e.g. XpmASLodgementsATO"
            />
            <p className="text-xs text-gray-400 mt-1">
              {isEdit
                ? 'Fixed once saved — it is the record key. Delete the set to change the table.'
                : 'Editable when creating a new set; fixed once saved.'}
            </p>
          </div>
          <Space size={8} className="pb-6">
            <Switch checked={isActive} onChange={setIsActive} size="small" />
            <span className="text-sm text-gray-700">Set active</span>
          </Space>
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <Space size={8}>
            <Switch checked={hasViews} onChange={setHasViews} size="small" />
            <span className="text-sm text-gray-700">Table has a dependent view</span>
          </Space>
          <p className="text-xs text-gray-400 mt-2">
            On a Replace upload, the import worker drops and recreates this view.
          </p>

          {hasViews && (
            <Space direction="vertical" size={12} className="w-full mt-3">
              <div style={{ maxWidth: 320 }}>
                <label className="block text-sm font-medium text-gray-700 mb-1">View name</label>
                <Input
                  value={viewName}
                  onChange={(e) => setViewName(e.target.value)}
                  placeholder="e.g. LodgementSummary"
                />
              </div>

              <div style={{ maxWidth: 420 }}>
                <label className="block text-sm font-medium text-gray-700 mb-1">View template</label>
                <Select
                  className="w-full"
                  showSearch
                  allowClear
                  value={viewDefinition || undefined}
                  onChange={(value) => setViewDefinition(value ?? '')}
                  loading={viewTemplatesQuery.isLoading}
                  placeholder="Select a view SQL template"
                  optionFilterProp="label"
                  options={(viewTemplatesQuery.data ?? []).map((template) => ({
                    value: template.key,
                    label: template.name,
                  }))}
                  notFoundContent={viewTemplatesQuery.isLoading ? 'Loading…' : 'No templates found'}
                />
                <p className="text-xs text-gray-400 mt-1">
                  The template's SQL (with <code>{'{{schema}}'}</code> substituted) is used to recreate the view.
                </p>
              </div>

              <div style={{ maxWidth: 320 }}>
                <label className="block text-sm font-medium text-gray-700 mb-1">View owner</label>
                <Input value={viewOwner} onChange={(e) => setViewOwner(e.target.value)} placeholder="e.g. service3" />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Grants</label>
                  <Button size="small" icon={<PlusOutlined />} onClick={addGrant}>
                    Add grant
                  </Button>
                </div>
                {viewGrants.length === 0 ? (
                  <p className="text-xs text-gray-400">No grants — the recreated view uses default privileges.</p>
                ) : (
                  <Space direction="vertical" size={8} className="w-full">
                    {viewGrants.map((grant, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          value={grant.privilege}
                          onChange={(e) => updateGrant(index, { privilege: e.target.value })}
                          placeholder="Privilege (e.g. SELECT)"
                          style={{ width: 220 }}
                        />
                        <Input
                          value={grant.grantee}
                          onChange={(e) => updateGrant(index, { grantee: e.target.value })}
                          placeholder="Grantee (e.g. reader)"
                          style={{ width: 220 }}
                        />
                        <Tooltip title="Remove grant">
                          <Button danger icon={<DeleteOutlined />} onClick={() => removeGrant(index)} />
                        </Tooltip>
                      </div>
                    ))}
                  </Space>
                )}
              </div>
            </Space>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-700">Rules in this set</span>
            <Button icon={<PlusOutlined />} onClick={openAddEntry}>
              Add rule
            </Button>
          </div>
          <Table<RuleEntry>
            rowKey={(row, index) => `${row.columnName}-${row.ruleType}-${index}`}
            loading={isEdit && ruleSetQuery.isLoading}
            columns={columns}
            dataSource={rules}
            pagination={false}
            locale={{ emptyText: 'No rules yet — add one to get started.' }}
          />
          <p className="text-xs text-gray-400 mt-2">
            The whole set is saved at once. Entries have no id — column + rule type identify them.
          </p>
        </div>
      </Space>

      <RuleEntryModal
        open={entryModalOpen}
        entry={editingIndex != null ? rules[editingIndex] ?? null : null}
        takenKeys={takenKeys}
        onCancel={() => setEntryModalOpen(false)}
        onSave={handleEntrySave}
      />
    </Drawer>
  )
}
